import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDate, API } from "@/lib/api";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle } from "@phosphor-icons/react";

export default function CheckIn() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", street: "", postal_code: "", city: "",
    country: "Deutschland", phone: "", email: "", guests_count: 1,
    id_number: "", signature: "", gdpr_consent: false,
    special_requests: "", breakfast: false, lunch: false, dinner: false
  });

  useEffect(() => {
    axios.get(`${API}/public/checkin/${token}`)
      .then(r => {
        setData(r.data);
        setForm(f => ({ ...f,
          first_name: r.data.guest.first_name, last_name: r.data.guest.last_name,
          email: r.data.guest.email || "", phone: r.data.guest.phone || "",
          guests_count: r.data.booking.guests || 1
        }));
      })
      .catch(e => setErr(e.response?.data?.detail || "Ungültiger Link"));
  }, [token]);

  const submit = async () => {
    if (!form.gdpr_consent) { setErr("Bitte DSGVO-Zustimmung erteilen"); return; }
    try {
      await axios.post(`${API}/public/checkin/${token}`, form);
      setOk(true);
    } catch (e) { setErr(e.response?.data?.detail || "Fehler"); }
  };

  if (err && !data) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="msb-card max-w-md"><h2 className="text-xl font-semibold">Fehler</h2><p className="text-sm mt-2">{err}</p></div>
    </div>
  );
  if (!data) return null;

  if (ok || data.already_completed) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="msb-card max-w-md text-center">
        <CheckCircle size={64} weight="duotone" className="mx-auto text-primary"/>
        <h2 className="text-2xl font-bold mt-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Check-in abgeschlossen</h2>
        <p className="text-muted-foreground mt-2">Vielen Dank! Wir freuen uns auf Ihre Anreise.</p>
      </div>
    </div>
  );

  const F = (k, label, type="text") => (
    <div><Label>{label}</Label>
      <Input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="rounded-xl mt-1.5" type={type} data-testid={`checkin-${k}`}/></div>
  );

  return (
    <div className="min-h-screen py-10 px-4 bg-background">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="text-primary font-bold text-lg" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</div>
          <h1 className="text-3xl font-bold mt-2" style={{fontFamily:"'Work Sans',sans-serif"}}>Online Check-in</h1>
          <p className="text-muted-foreground mt-1">Willkommen! Bitte füllen Sie das Formular aus.</p>
        </div>

        <div className="msb-card">
          <h3 className="font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Ihre Buchung</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs uppercase text-muted-foreground">Zimmer</div><div>{data.booking.room}</div></div>
            <div><div className="text-xs uppercase text-muted-foreground">Nächte</div><div>{data.booking.nights}</div></div>
            <div><div className="text-xs uppercase text-muted-foreground">Anreise</div><div>{formatDate(data.booking.check_in)}</div></div>
            <div><div className="text-xs uppercase text-muted-foreground">Abreise</div><div>{formatDate(data.booking.check_out)}</div></div>
          </div>
        </div>

        <div className="msb-card space-y-4">
          <h3 className="font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>Persönliche Daten</h3>
          <div className="grid grid-cols-2 gap-4">
            {F("first_name","Vorname")}{F("last_name","Nachname")}
            {F("street","Straße & Nr.")}{F("postal_code","PLZ")}
            {F("city","Ort")}{F("country","Land")}
            {F("phone","Telefon")}{F("email","E-Mail","email")}
            {F("id_number","Ausweis-/Passnummer (optional)")}
            <div><Label>Personen</Label><Input type="number" min="1" value={form.guests_count} onChange={e=>setForm({...form,guests_count:+e.target.value})} className="rounded-xl mt-1.5"/></div>
          </div>

          <div>
            <Label>Sonderwünsche</Label>
            <Textarea value={form.special_requests} onChange={e=>setForm({...form,special_requests:e.target.value})} className="rounded-xl mt-1.5"/>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Verpflegung</div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.breakfast} onCheckedChange={v=>setForm({...form,breakfast:!!v})} data-testid="checkin-breakfast"/> Frühstück</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.lunch} onCheckedChange={v=>setForm({...form,lunch:!!v})}/> Mittagessen</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.dinner} onCheckedChange={v=>setForm({...form,dinner:!!v})}/> Abendessen</label>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={form.gdpr_consent} onCheckedChange={v=>setForm({...form,gdpr_consent:!!v})} data-testid="checkin-gdpr"/>
            <span>Ich stimme der Speicherung meiner Daten gemäß DSGVO zu.</span>
          </label>

          {err && <div className="text-sm text-destructive">{err}</div>}

          <Button onClick={submit} className="w-full h-11 rounded-xl bg-primary" data-testid="checkin-submit">
            Check-in abschließen
          </Button>
        </div>
      </div>
    </div>
  );
}
